import { ArrowUpRight, Instagram, Youtube, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { Eyebrow, MaskLine, Reveal } from "@/components/site/Reveal";

const DISCIPLINES = ["Rope Rescue", "Confined Space Rescue", "Structural Collapse Rescue", "Vehicle Machinery Rescue (VMR)", "Trench Rescue", "Standby Rescue Services"];

const SOCIAL = [
  ["website", "trt365.com", "https://trt365.com/", ArrowUpRight],
  ["instagram", "@soflotrt", "https://www.instagram.com/Soflotrt/", Instagram],
  ["youtube", "YouTube", "https://www.youtube.com/@soflotrt", Youtube],
  ["facebook", "Facebook", "https://www.facebook.com/SoFloTRT/", Facebook],
];

const PHOTOS = [
  ["/img/soflo-IMG_1596.webp", "Rope rescue training", "aspect-[3/4] sm:aspect-auto sm:row-span-2"],
  ["/img/soflo-IMG_2941.webp", "Confined space rescue", "aspect-[4/3] sm:aspect-auto"],
  ["/img/soflo-IMG_2627.webp", "Military team training", "aspect-[4/3] sm:aspect-auto"],
];

export function Partner() {
  return (
    <section id="partner" data-testid="partner-section" className="grain relative overflow-hidden border-y border-white/10 bg-obsidian py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Eyebrow>Official Sales &amp; Training Partner</Eyebrow>
            <h2 className="mt-5 font-display text-[12.5vw] sm:text-6xl md:text-8xl font-black uppercase leading-[0.85]" style={{ fontWeight: 900 }}>
              <MaskLine inView>Trained by</MaskLine>
              <MaskLine inView delay={0.1}><span className="text-volt">SoFLO TRT.</span></MaskLine>
            </h2>
            <Reveal className="mt-10">
              <img src="/img/soflo-logo.webp" alt="SoFLO Technical Rescue Training logo" data-testid="partner-logo" className="w-64 md:w-80" />
            </Reveal>
            <Reveal delay={0.1} className="mt-10 max-w-xl">
              <blockquote className="border-l-2 border-[var(--volt)] pl-6">
                <p className="text-base md:text-lg leading-relaxed text-zinc-200">
                  "SoFLO TRT was born out of necessity. Technical Rescue Operators were starving for training that meant more than a certification printed on cheap paper. So, we decided to give it to them by providing the most realistic training courses found anywhere."
                </p>
                <footer className="mt-4 font-display text-3xl md:text-4xl font-black uppercase text-volt">No retakes. No second chances.</footer>
              </blockquote>
            </Reveal>
            <Reveal delay={0.15} className="mt-10">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Training disciplines</p>
              <ul className="grid gap-x-6 sm:grid-cols-2 border-t border-white/10">
                {DISCIPLINES.map((d, i) => (
                  <li key={d} data-testid={`partner-discipline-${i}`} className="flex items-baseline gap-3 border-b border-white/10 py-3">
                    <span className="font-mono text-[11px] text-volt">0{i + 1}</span>
                    <span className="font-display text-xl md:text-2xl font-bold uppercase">{d}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-6 flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:auto-rows-[300px] lg:auto-rows-[320px]">
              {PHOTOS.map(([src, label, cls], i) => (
                <Reveal key={src} delay={i * 0.08} className={`group relative overflow-hidden ${i === 0 ? "clip-notch" : ""} ${cls}`}>
                  <img src={src} alt={`SoFLO TRT ${label}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <span className="absolute bottom-4 left-4 font-mono text-[11px] uppercase tracking-widest text-white">{label}</span>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1} className="border border-white/10 bg-surface p-6 md:p-8">
              <div className="grid gap-4 sm:grid-cols-3">
                <a href="tel:+15615436695" data-testid="partner-phone-link" className="flex items-center gap-3 hover:text-volt transition-colors"><Phone className="h-4 w-4 text-volt" /><span className="font-mono text-sm">561-543-6695</span></a>
                <a href="mailto:info@TRT365.com" data-testid="partner-email-link" className="flex items-center gap-3 hover:text-volt transition-colors"><Mail className="h-4 w-4 text-volt" /><span className="font-mono text-sm">info@TRT365.com</span></a>
                <p className="flex items-center gap-3"><MapPin className="h-4 w-4 shrink-0 text-volt" /><span className="font-mono text-sm">3400 N 56th Ave, Hollywood, FL 33021</span></p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {SOCIAL.map(([k, label, href, Icon]) => (
                  <a key={k} href={href} target="_blank" rel="noopener noreferrer" data-testid={`partner-${k}-link`} className="flex h-11 items-center gap-2 border border-white/15 px-4 font-mono text-xs uppercase tracking-widest transition-colors hover:border-[var(--volt)] hover:text-volt">
                    <Icon className="h-4 w-4" /> {label}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
